import {MaintainAppName} from '@hconnect/common/hproduce/maintain.types'
import {Env} from '@hconnect/common/hproduce/types'
import {
  Notifications,
  TranslationKey,
  TranslationActionKey,
  Translations,
  NotificationsOptions,
  generateNotificationChannels
} from '@hconnect/common/notifications'
import {useLoginContext, useUserContext} from '@hconnect/login'
import {Shell, CommonHeader, useGuidedTours, TourBanner} from '@hconnect/uikit/src/lib2'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import i18n from 'i18next'
import {useEffect, useMemo} from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'
import {Navigate, Outlet, useLocation, useNavigate, useParams} from 'react-router'

import {useAppsByWhiteList} from '../../dashboard/hooks/useAppsByWhiteList'
import {useGlobalContext} from '../hooks/useGlobalContext'
import {supportedLanguages} from '../localization'
import {getNavItems} from '../routing'

import {MaintainErrorFallback} from './MaintainErrorFallback'

type MaintainUrlApp = 'rcfa' | 'pm'

const maintainAppMap: Record<MaintainUrlApp, MaintainAppName> = {
  rcfa: 'Rcfa',
  pm: 'PM'
}

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url)

const NOTIFICATIONS_TRANSLATION_KEYS: Translations = {
  label: {
    [TranslationKey.TODAY]: 'notifications.label.today',
    [TranslationKey.YESTERDAY]: 'notifications.label.yesterday',
    [TranslationKey.NOTIFICATIONS]: 'notifications.label.notifications',
    [TranslationKey.EMPTY]: 'notifications.label.empty',
    [TranslationKey.ERROR]: 'error.label.fetchingError',
    [TranslationKey.SHOULD_DELETE]: 'notifications.label.shouldDeleteNotification'
  },
  action: {
    [TranslationActionKey.DELETE]: 'notifications.action.delete',
    [TranslationActionKey.CONFIRM_DELETE]: 'notifications.action.confirmDelete',
    [TranslationActionKey.CANCEL]: 'action.cancel'
  }
}

const getSelectedApp = (path: string): MaintainAppName => {
  const parts = path.split('/')
  if (parts.length < 3) return 'default'
  const maybeMaintainUrlApp = parts[3]
  if (Object.keys(maintainAppMap).includes(maybeMaintainUrlApp)) {
    return maintainAppMap[maybeMaintainUrlApp]
  }
  return 'default'
}

export const Layout = () => {
  const {t} = useTranslation()
  const {plantId} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const selectedApp: MaintainAppName = getSelectedApp(location.pathname)
  const globalContext = useGlobalContext().state
  const apps = useAppsByWhiteList(globalContext.whiteList, plantId)
  const {logout, isLoggedIn} = useLoginContext()
  const {loggedUser} = useUserContext()

  const upmPlants = useUpmPlantsContext()

  const appOptions = globalContext.whiteList.optionsPerApp.Maintain
  const plantNotAllowed = Boolean(plantId) && !appOptions?.some((opt) => opt.plantId === plantId)
  const headerUser = {email: loggedUser?.eMail ?? undefined, name: loggedUser?.name ?? undefined}

  const {tours, tourStatuses} = useGuidedTours()

  const navItems = useMemo(() => {
    if (!plantId) return []
    return getNavItems(selectedApp, plantId, t, apps.allowed)
  }, [plantId, apps.allowed, selectedApp, t])

  useEffect(() => {
    i18n.cloneInstance({
      supportedLngs: supportedLanguages[selectedApp]
    })
  }, [selectedApp])

  const notificationOptions = useMemo<NotificationsOptions | null>(() => {
    const channels = generateNotificationChannels(globalContext.whiteList)
    if (!loggedUser?.country) return null
    return {
      channels,
      countryCode: loggedUser.country
    }
  }, [globalContext.whiteList, loggedUser?.country])

  if (plantNotAllowed) {
    return <Navigate to={'/'} replace={true} />
  }

  return (
    <Shell
      isResponsive
      boxed={false}
      header={
        <CommonHeader
          additionalActions={
            isLoggedIn && notificationOptions !== null
              ? [
                  <Notifications
                    key="notification-provider"
                    options={notificationOptions}
                    translations={NOTIFICATIONS_TRANSLATION_KEYS}
                  />
                ]
              : undefined
          }
          appName="Maintain"
          plant={plantId ? {plantId, plantName: getPlantName(upmPlants, plantId)} : undefined}
          upmPlants={upmPlants}
          onLogout={logout}
          supportedLanguages={supportedLanguages[selectedApp]}
          user={headerUser}
          env={process.env.REACT_APP_STAGE as Env}
          whiteList={globalContext.whiteList}
          navItems={navItems}
          onNavItemClick={(url) => {
            if (isAbsoluteUrl(url)) {
              window.open(url, '_blank', 'noopener,noreferrer')
            }
          }}
          navigate={(url) => {
            if (isAbsoluteUrl(url)) return
            void navigate(url)
          }}
          locationPath={location.pathname}
          guidedTours={{tours, statuses: tourStatuses}}
          footer={{copyrightNote: t('footer.copyrightNote')}}
        />
      }
    >
      <ErrorBoundary FallbackComponent={MaintainErrorFallback} resetKeys={[location.pathname]}>
        <TourBanner sx={{mt: 3, mx: {xs: 2, lg: 4}}} />
        <Outlet />
      </ErrorBoundary>
    </Shell>
  )
}
